/**
 * WHOSE TICKETS THE DESK'S FIGURES COUNT, in words.
 *
 * Performance and the open count beside each name in the hand-over list read
 * the tickets of the reader's own brands, and the API names those brands in
 * `restrictedTo` — null for a reader whose access covers every brand. Every
 * sentence here is built from that answer, the same one the figures came in,
 * so the words cannot claim a breadth the numbers beside them do not have.
 * Performance used to say, in a fixed sentence, that its rows covered every
 * brand; for a restricted reader that is no longer true.
 *
 * Brands are listed as the Fund audit trail and Recent actions list them, and
 * a restriction is put in the same words, so a restricted reader hears one
 * voice across the console.
 */

/** Performance's caption, the sentence that closes its note, and its empty state. */
export interface PerformanceWords {
  caption: string;
  scope: string;
  emptyTitle: string;
  emptyBody: string;
}

export function performanceWords(restrictedTo: readonly string[] | null): PerformanceWords {
  /* An empty list names no brand: it is read as no restriction, never as "restricted to ,". */
  if (restrictedTo === null || restrictedTo.length === 0) {
    return {
      caption: 'Per-agent support performance across every ticket assigned to that agent',
      scope:
        'These rows cover every brand: the brand filter at the top of the console does not narrow them.',
      emptyTitle: 'No agent has handled a ticket',
      emptyBody:
        'Only active admins holding at least one assigned ticket are listed, and right now no active admin holds one.',
    };
  }
  const names = restrictedTo.join(', ');
  const those = restrictedTo.length === 1 ? 'that brand' : 'those brands';
  return {
    caption: `Per-agent support performance across the tickets assigned to that agent in ${names}`,
    scope:
      `Your access is restricted to ${names}, so these rows cover ${those} alone, and the brand ` +
      'filter at the top of the console does not narrow them further: a ticket an agent holds in ' +
      `any other brand is not counted, and an agent who holds no ticket in ${those} is not listed.`,
    emptyTitle: `No active agent holds a ticket in ${names}`,
    emptyBody:
      `Your access is restricted to ${names}, so this covers ${those} alone, and no active admin ` +
      `holds a ticket in ${restrictedTo.length === 1 ? 'it' : 'them'}. A ticket in any other brand ` +
      'is not counted here, so this says nothing about those.',
  };
}

/**
 * The sentence beside the hand-over list's open counts, or null when the
 * reader sees every brand and each count is the person's whole open load. An
 * empty list names no brand and is read the same way.
 */
export function openCountWords(restrictedTo: readonly string[] | null): string | null {
  if (restrictedTo === null || restrictedTo.length === 0) return null;
  const those = restrictedTo.length === 1 ? 'that brand' : 'those brands';
  return (
    `Your access is restricted to ${restrictedTo.join(', ')}, so the open count beside each name ` +
    `is of their tickets in ${those} alone.`
  );
}
